'use strict';

const _ = require('underscore');
const Backbone = require('backbone');
const PartialPageConfiguration = require('framework/domain/partial-page/partial-page-configuration');
const PartialPagination = require('framework/domain/partial-page/partial-pagination');

class ItemCollection extends Backbone.Collection {
    /**
     *
     * @param models {Backbone.Model[]}
     * @param options {Object}
     * @param options.configuration {PartialPageConfiguration}
     */
    constructor(models, options) {
        super(models, options);

        this._configuration = options && options.configuration;

        if (!(this._configuration instanceof PartialPageConfiguration)) {
            throw new Error(`Invalid parameter of configuration: ${this._configuration}`);
        }
        if (this.isEmpty()) {
            throw new Error(`PartialPage ItemCollection must have least one models.`)
        }
    }

    /**
     *
     * @return {number}
     * @private
     */
    _currentNowNumber() {
        return this._configuration.currentNowNumber();
    }

    /**
     *
     * @return {Array}
     * @private
     */
    _numbers() {
        return _.map(this.models, this._configuration.modelToNumber);
    }

    /**
     * PartialPage の取り扱う時間範囲の最小値
     *
     * @return {number}
     */
    min() {
        if (this.isEmpty()) {
            return this._currentNowNumber();
        }
        return _.min(this._numbers());
    }

    /**
     * PartialPage の取り扱う時間範囲の最大値
     *
     * @return {number}
     */
    max() {
        if (this.isEmpty()) {
            return this._currentNowNumber();
            // return Infinity;
        }
        return _.max(this._numbers());
    }

    /**
     * もう一つの ItemCollection に含まれるモデルが、自身にも含まれるか否かを返す
     *
     * @param other {ItemCollection}
     * @return {boolean}
     */
    contains(other) {
        if (!(other instanceof ItemCollection)) {
            throw new Error(`Invalid itemCollection: ${other}`);
        }

        return _.some(_.intersection(this.modelIDs(), other.modelIDs()));
    }

    modelIDs() {
        return _.pluck(this.models, 'id');
    }
}

class PartialPage extends Backbone.Model {
    /**
     * 時系列コレクションの最新部分・途中部分を取り扱うための Collection クラス
     *
     * PartialPage の振る舞いは PartialPageConfiguration の継承先で規定される。
     *
     * @param attributes {Object}
     * @param attributes.configuration {PartialPageConfiguration}
     * @param attributes.pagination {PartialPagination}
     * @param attributes.biggerSidePartialPage {PartialPage|undefined}
     * @param attributes.smallSidePartialPage {PartialPage|undefined}
     * @param options {Object}
     * @param options.items {Backbone.Model[]}
     */
    constructor(attributes, options) {
        super(attributes, options);

        const configuration = this.configuration();
        const pagination = this.pagination();

        if (!(configuration instanceof PartialPageConfiguration)) {
            throw new Error(`Invalid parameter of configuration: ${configuration}`);
        }
        if (!(pagination instanceof PartialPagination)) {
            throw new Error(`Invalid parameter of pagination: ${pagination}`);
        }

        const collectionOptions = {
            configuration: configuration,
        };
        this._items = new ItemCollection(options.items, collectionOptions);

        this._items.comparator = (a, b) => {
            return configuration.modelToNumber(a) - configuration.modelToNumber(b);
        };
        this._items.sort();
    }

    _modelToNumber(model) {
        return this.configuration().modelToNumber(model);
    }

    /**
     * この PartialPage の振る舞いを定義する Configuration オブジェクト
     *
     * @return {PartialPageConfiguration}
     */
    configuration() {
        return this.get('configuration');
    }

    /**
     * この PartialPage の前後ページの情報を表す Pagination オブジェクト
     *
     * @return {PartialPagination}
     */
    pagination() {
        return this.get('pagination');
    }

    /**
     * この PartialPage の前後ページの情報を表す Pagination を更新する
     *
     * @param pagination {PartialPagination}
     */
    setPagination(pagination) {
        this.set('pagination', pagination);
    }

    /**
     * 大きい側で接しているページ（があれば）返す
     *
     * @return {PartialPage|undefined}
     */
    getBiggerSidePartialPage() {
        return this.get('biggerSidePartialPage');
    }

    /**
     * 大きい側で接しているページを設定する
     *
     * @param partialPage {PartialPage}
     */
    setBiggerSidePartialPage(partialPage) {
        this.set('biggerSidePartialPage', partialPage);
    }

    /**
     * 小さい側で接しているページ（があれば）返す
     *
     * @return {PartialPage|undefined}
     */
    getSmallSidePartialPage() {
        return this.get('smallSidePartialPage');
    }

    /**
     * 小さい側で接しているページを設定する
     *
     * @param partialPage {PartialPage}
     */
    setSmallSidePartialPage(partialPage) {
        this.set('smallSidePartialPage', partialPage);
    }

    /**
     * このページを含む、隣接したページの一覧
     *
     * @param direction
     * @return {[]}
     */
    seriesOfPages(direction=null) {
        const pages = [this];

        if (this.getBiggerSidePartialPage() && (direction == null || direction === "next")) {
            pages.push(this.getBiggerSidePartialPage().seriesOfPages("next"));
        }
        if (this.getSmallSidePartialPage() && (direction == null || direction === "prev")) {
            pages.push(this.getSmallSidePartialPage().seriesOfPages("prev"));
        }

        return _.sortBy(_.flatten(pages), partialPage => { return partialPage.min(); });
    }

    /**
     * この PartialPage に属する Item の Collection を返す
     *
     * @return {ItemCollection}
     */
    itemCollection() {
        return this._items;
    }

    /**
     * この PartialPage に属する Item の個数を返す
     *
     * @return {number}
     */
    itemCount() {
        return this.itemCollection().length;
    }

    /**
     * このPartialPageにitemを追加する
     *
     * @param item {Backbone.Model}
     */
    addItem(item) {
        const options = {
            at: _.sortedIndex(
                _.map(
                    this.itemCollection().models,
                    (m) => {
                        return this._modelToNumber(m);
                    }
                ),
                this._modelToNumber(item)
            )
        };

        this.itemCollection().add(item, options);
    }

    /**
     * PartialPage の取り扱う時間範囲の最小値
     *
     * @return {number}
     */
    min() {
        return this._items.min();
    }

    /**
     * PartialPage の取り扱う時間範囲の最大値
     *
     * @return {number}
     */
    max() {
        return this._items.max();
    }

    /**
     * 自身とotherの保持するモデルに重複があるとき、自身よりも小さい側の重なっていない範囲の新しいPartialPageを作成して返す
     * 自身から見ると新しいPartialPageは小さい側の隣接ページに、新しいPartialPageから見ると自身は大きい側の隣接ページとなっている。
     *
     * @param other {PartialPage}
     * @return {PartialPage|null}
     */
    buildSmallSideSplitPartialPage(other) {
        // モデル要素が重複していない場合には、ページ同士が重なっていない
        if (!this.contains(other)) {
            return null;
        }

        const items =_.filter(
            other.itemCollection().models,
            (/**Backbone.Model*/model) => {
                return this._modelToNumber(model) < this.min();
            }
        );

        if (_.isEmpty(items)) {
            return null;
        }

        return new PartialPage(
            {
                configuration: this.configuration(),
                pagination: other.pagination().clearPrevSide(),
                smallSidePartialPage: other.getSmallSidePartialPage(),
                biggerSidePartialPage: this,
            },
            {
                items: items
            }
        );
    }

    /**
     * 自身とotherの保持するモデルに重複があるとき、自身よりも大きい側の重なっていない範囲の新しいPartialPageを作成して返す。
     * 自身から見ると新しいPartialPageは大きい側の隣接ページに、新しいPartialPageから見ると自身は小さい側の隣接ページとなっている。
     *
     * @param other {PartialPage}
     * @return {PartialPage|null}
     */
    buildBiggerSideSplitPartialPage(other) {
        // モデル要素が重複していない場合には、ページ同士が重なっていない
        if (!this.contains(other)) {
            return null;
        }

        const items =_.filter(
            other.itemCollection().models,
            (/**Backbone.Model*/model) => {
                return this._modelToNumber(model) > this.max();
            }
        );

        if (_.isEmpty(items)) {
            return null;
        }

        return new PartialPage(
            {
                configuration: this.configuration(),
                pagination: other.pagination().clearNextSide(),
                smallSidePartialPage: this,
                biggerSidePartialPage: other.getBiggerSidePartialPage(),
            },
            {
                items: items
            }
        );
    }

    /**
     * もう一つの PartialPage に含まれるモデルが、自身にも含まれるか否かを返す
     *
     * @param other {PartialPage}
     * @return {boolean}
     */
    contains(other) {
        if (!(other instanceof PartialPage)) {
            throw new Error(`Invalid partialPage: ${other}`);
        }

        return this.itemCollection().contains(other.itemCollection());
    }

    /**
     * もう一つの PartialPage と重なっているか
     *
     * @param other {PartialPage}
     * @return {boolean}
     */
    isOverlapped(other) {
        return this.contains(other);
    }
}

module.exports = PartialPage;



// WEBPACK FOOTER //
// ./framework/domain/partial-page/partial-page.js